

import {
  IconAlertCircle,
  IconArchive,
  IconBuildingStore,
  IconCalendar,
  IconCalendarCancel,
  IconCalendarExclamation,
  IconCheck,
  IconClipboardList,
  IconCookie,
  IconLayoutBoard,
  IconMail,
  IconPalette,
  IconPaperclip,
  IconQuestionMark,
  IconServer,
  IconTicket,
  IconUserPlus,
  IconUsersGroup,
  IconWorldOff,
  IconShieldLock,
  IconBrush,
  IconTypography,
  IconHandClick,
  IconCode,
  IconForms,
  IconShare,
  IconAdjustments,
  IconFiles,
  IconSettings,
  IconLanguage,
  IconToggleLeft,
  IconLock,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

const ICONS = {
  fields: IconQuestionMark,
  companions: IconUserPlus,
  register: IconTicket,
  themeEditor: IconPalette,
  cookies: IconCookie,
  websiteStatus: IconWorldOff,
  customisation: IconAdjustments,
  // 2026-05-28: Website Settings modal consolidated to 3 tabs. Old ids
  // above are kept (reused by other surfaces e.g. PortalSettingsPanel).
  statusAccess: IconWorldOff,
  sharingCookies: IconShare,
  advanced: IconAdjustments,
  offering: IconBuildingStore,
  treatments: IconClipboardList,
  lanes: IconLayoutBoard,
  calendar: IconCalendar,
  conflictsAppointments: IconCalendarExclamation,
  cancelledAppointments: IconCalendarCancel,
  general: IconMail,
  recipient: IconCheck,
  warnings: IconAlertCircle,
  appendix: IconPaperclip,
  sender: IconMail,
  domainEntries: IconServer,
  guestsBlocked: IconUserPlus,
  plan: IconLayoutBoard,
  collaborators: IconUsersGroup,
  archive: IconArchive,
  sensitive: IconShieldLock,
  assignment: IconUsersGroup,
  design: IconBrush,
  typography: IconTypography,
  button: IconHandClick,
  formFields: IconForms,
  customCss: IconCode,
  socialPreview: IconShare,
  documents: IconFiles,
  settings: IconSettings,
  texts: IconLanguage,
  options: IconToggleLeft,
};

export const TabSwitcher = ({
  currentTab,
  onTabChange,
  counts,
  tabs,
  icon = true,
  clickable = true,
  lockedTabs,
  dense = false,
}: {
  currentTab: string;
  counts?: (number | undefined)[];
  tabs: string[];
  onTabChange?: (tabId: string) => void;
  icon?: boolean;
  clickable?: boolean;
  // Tabs that are visible but disabled (e.g. a registration tab while the
  // event is LOCKED). Rendered muted with a lock marker instead of a count.
  lockedTabs?: string[];
  // Narrow-rail fit (e.g. the 320px builder side panel): tighter pill
  // padding + tab gap so four text tabs stay on one line without clipping.
  dense?: boolean;
}) => {
  const t = useTranslations("tabSwitcher");
  const handleTabClick = (tabId: string) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  return (
    <div
      className={`flex border-b w-full relative overflow-x-auto outline-quaternary ${
        dense ? "space-x-1" : "space-x-4"
      }`}
    >
      {tabs.map((tab, index) => {
        const Icon = icon ? ICONS[tab as keyof typeof ICONS] : undefined;
        const count = counts ? counts[index] : undefined;
        const isActive = currentTab === tab;
        const isLocked = lockedTabs?.includes(tab) ?? false;

        return (
          <div key={tab} className="relative">
            <button
              className={`flex py-2 ${!clickable ? "cursor-default" : ""} ${
                !clickable && !isActive ? "opacity-50" : ""
              }`}
              disabled={!clickable}
              onClick={() => handleTabClick(tab)}
            >
              <div
                className={`flex w-full rounded-lg body-3-medium flex-row items-center whitespace-nowrap ${
                  dense ? "px-1.5 py-1.5 gap-1.5" : "p-2 gap-2"
                }
                ${
                  isActive
                    ? "font-primary border outline-tertiary"
                    : isLocked
                      ? "font-tertiary"
                      : "font-secondary"
                }`}
              >
                {Icon !== undefined && <Icon size={16} />}
                <span className={icon ? "hidden sm:inline" : ""}>{t(tab)}</span>
                {isLocked ? (
                  <IconLock size={14} className="font-tertiary" />
                ) : (
                  count !== undefined && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 surface-tertiary rounded-full body-3-medium">
                      {count}
                    </span>
                  )
                )}
              </div>
            </button>

            {isActive && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 surface-dark"
                layoutId="activeTabIndicator"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
